/**
 * FP3-028 / FP3-028A — Login page.
 *
 * Invite-only sign-in for Frame Practice. There is intentionally no
 * signup link — a "Request Access" callout points to /request-access
 * for users who don't yet have an invite. Preview demo credentials
 * are only ever surfaced when the backend reports is_preview=true.
 *
 * Visual: warm cream background, deep green primary, single calm
 * card on mobile / two-column on desktop. Matches the rest of the
 * Frame Practice product shell (Sidebar palette + Manrope heading).
 */
import { useEffect, useState } from "react";
import { Link, Navigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import { CaretDown, CaretUp, ShieldCheck, Lock, Sparkle, ArrowRight } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import PasswordInput from "@/components/ui/PasswordInput";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { useAuth } from "@/lib/auth";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEMO_ACCOUNTS = [
  { email: "sarah@framepractice.demo", label: "Sarah Mitchell", role: "SLP · provider" },
  { email: "riley@framepractice.demo", label: "Riley Hayes", role: "Special Education Teacher · provider" },
  { email: "admin@framepractice.demo", label: "Demo Admin", role: "admin" },
];

export default function Login() {
  const { user, login, loading } = useAuth();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [isPreview, setIsPreview] = useState(false);
  const [demoOpen, setDemoOpen] = useState(false);

  useEffect(() => {
    axios
      .get(`${API_URL}/preview/flags`)
      .then((r) => setIsPreview(Boolean(r.data?.is_preview)))
      .catch(() => setIsPreview(false));
  }, []);

  if (!loading && user) {
    // FP3-029B3 — Admin family lands on /admin/dashboard by default
    // (was: /). Provider users still land on /. The `state.from`
    // path (set by ProtectedRoute when an unauthed user hit a deep
    // link) wins over both so the redirect chain works.
    const adminFamily = ["admin", "org_admin", "super_admin"].includes(
      user.permission_role || ""
    );
    const defaultPath = adminFamily ? "/admin/dashboard" : "/";
    const from = location.state?.from || defaultPath;
    return <Navigate to={from} replace />;
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setErrorMsg("");
    try {
      await login(email.trim().toLowerCase(), password);
      toast.success("Welcome back");
    } catch (err) {
      const status = err?.response?.status;
      const msg =
        status === 403
          ? "Your account isn't active. Reach out to your admin."
          : "That email and password don't match. Please try again.";
      setErrorMsg(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const fillDemo = (e) => {
    setEmail(e);
    setPassword("DemoAdmin!2026");
    setErrorMsg("");
  };

  return (
    <div
      data-testid="login-page"
      className="min-h-screen bg-[#F7F5EF] body-font"
    >
      <div className="mx-auto max-w-6xl px-5 sm:px-8 py-10 lg:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-[1.05fr_1fr] gap-10 lg:gap-16 items-center">
          {/* Brand + product framing — desktop only as a calm intro
              column; collapses into a small wordmark on mobile. */}
          <div data-testid="login-brand" className="hidden lg:block">
            <div className="flex items-center gap-3 mb-8">
              <div className="w-11 h-11 rounded-xl bg-[#1E3F33] flex items-center justify-center shadow-sm">
                <span className="text-white text-lg font-bold heading-font">F</span>
              </div>
              <div className="leading-tight">
                <div className="text-[20px] font-bold text-[#1E3F33] heading-font tracking-tight">
                  Frame<span className="text-[#8FA998] font-medium ml-0.5">Practice</span>
                </div>
                <div className="text-[11px] uppercase tracking-[0.14em] text-[#8FA998] mt-0.5">
                  Provider workspace
                </div>
              </div>
            </div>

            <h2 className="text-[28px] xl:text-[32px] font-semibold text-[#1F2937] heading-font leading-[1.2] mb-4 max-w-md">
              A calm, connected workspace for school-based providers.
            </h2>
            <p className="text-[14px] text-[#4B5563] leading-relaxed max-w-md mb-8">
              Manage schedules, goals, sessions, data, and student support
              records in one place — without the noise of a generic
              productivity tool.
            </p>

            <div className="space-y-2.5 max-w-md">
              <FeatureRow
                icon={<ShieldCheck size={16} weight="duotone" className="text-[#1E3F33]" />}
                title="Pilot + invite access"
                body="Solo providers can start a Pilot workspace. School and district teams can request access or join by invite."
              />
              <FeatureRow
                icon={<Lock size={16} weight="duotone" className="text-[#1E3F33]" />}
                title="Quiet by design"
                body="Tools designed around a provider's day — not the other way around."
              />
            </div>
          </div>

          {/* Sign-in card */}
          <div className="w-full">
            {/* Mobile/tablet wordmark — appears above the card when
                the brand column is hidden. */}
            <div className="flex lg:hidden items-center gap-2.5 mb-6">
              <div className="w-9 h-9 rounded-lg bg-[#1E3F33] flex items-center justify-center">
                <span className="text-white text-sm font-bold heading-font">F</span>
              </div>
              <div className="leading-tight">
                <div className="text-[16px] font-bold text-[#1E3F33] heading-font tracking-tight">
                  Frame<span className="text-[#8FA998] font-medium ml-0.5">Practice</span>
                </div>
                <div className="text-[10px] uppercase tracking-[0.14em] text-[#8FA998]">
                  Provider workspace
                </div>
              </div>
            </div>

            <Card
              data-testid="login-card"
              className="border-[#E5E3DB] shadow-[0_2px_14px_rgba(31,41,55,0.04)] rounded-2xl"
            >
              <CardContent className="p-7 sm:p-8">
                <div className="mb-6">
                  <div
                    data-testid="login-invite-only-pill"
                    className="inline-flex items-center gap-1.5 text-[10.5px] uppercase tracking-[0.14em] text-[#1E3F33] bg-[#E8F0EB] rounded-full px-2.5 py-1 mb-3 font-semibold"
                  >
                    <ShieldCheck size={11} weight="duotone" />
                    Pilot + invite access
                  </div>
                  <h1 className="text-[24px] font-semibold text-[#1F2937] heading-font leading-tight">
                    Sign in
                  </h1>
                  <p className="text-[12.5px] text-[#6B7280] mt-1.5">
                    Welcome back. Continue to your workspace.
                  </p>
                </div>

                <form
                  data-testid="login-form"
                  onSubmit={onSubmit}
                  className="space-y-4"
                  noValidate
                >
                  <div>
                    <Label
                      htmlFor="login-email"
                      className="text-[11.5px] uppercase tracking-[0.1em] text-[#6B7280] font-semibold"
                    >
                      Email
                    </Label>
                    <Input
                      id="login-email"
                      data-testid="login-email"
                      type="email"
                      autoComplete="email"
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        if (errorMsg) setErrorMsg("");
                      }}
                      required
                      className="mt-1.5 h-10 border-[#E5E3DB] bg-white focus-visible:ring-[#1E3F33]/30 focus-visible:border-[#1E3F33]"
                      placeholder="you@school.org"
                    />
                  </div>
                  <div>
                    <Label
                      htmlFor="login-password"
                      className="text-[11.5px] uppercase tracking-[0.1em] text-[#6B7280] font-semibold"
                    >
                      Password
                    </Label>
                    <PasswordInput
                      id="login-password"
                      data-testid="login-password"
                      autoComplete="current-password"
                      value={password}
                      onChange={(e) => {
                        setPassword(e.target.value);
                        if (errorMsg) setErrorMsg("");
                      }}
                      required
                      className="mt-1.5 h-10 border-[#E5E3DB] bg-white focus-visible:ring-[#1E3F33]/30 focus-visible:border-[#1E3F33]"
                    />
                  </div>

                  {errorMsg && (
                    <div
                      data-testid="login-error"
                      role="alert"
                      className="text-[12.5px] text-[#7A3A2C] bg-[#F8EFEA] border border-[#E8D6CD] rounded-md px-3 py-2 leading-relaxed"
                    >
                      {errorMsg}
                    </div>
                  )}

                  <Button
                    type="submit"
                    data-testid="login-submit"
                    disabled={submitting}
                    className="w-full h-10 bg-[#1E3F33] hover:bg-[#173127] disabled:bg-[#1E3F33]/60 text-white rounded-lg text-sm font-medium tracking-tight transition-colors"
                  >
                    {submitting ? "Signing in…" : "Sign in"}
                  </Button>
                </form>

                <div className="mt-6 pt-5 border-t border-[#F1F2F4]">
                  <Link
                    to="/forgot-password"
                    data-testid="login-forgot-password-link"
                    className="text-[12.5px] text-[#6B7280] hover:text-[#1E3F33] transition-colors"
                  >
                    Forgot password?
                  </Link>

                  {/* FP3-069 follow-up #8 — Solo Pilot is now the
                      primary standout CTA below sign-in. The
                      Request-access link is preserved as a smaller
                      secondary action below it for schools / districts /
                      teams. Calm visual weight matches Frame Practice. */}
                  <div className="mt-5">
                    <Link
                      to="/start-pilot"
                      data-testid="login-start-pilot-cta"
                      className="block w-full text-center rounded-lg border border-[#1E3F33] bg-white hover:bg-[#F3F7F4] text-[#1E3F33] py-2.5 text-[14px] font-semibold tracking-tight transition-colors"
                    >
                      Start a Pilot Workspace
                    </Link>
                    <p
                      data-testid="login-start-pilot-subtext"
                      className="mt-1.5 text-center text-[11.5px] text-[#6B7280]"
                    >
                      Free access for a limited time.
                    </p>
                  </div>

                  <div className="mt-4 text-center text-[12px] text-[#6B7280]">
                    Joining a school or team?{" "}
                    <Link
                      to="/request-access"
                      data-testid="login-request-access-link"
                      className="text-[#1E3F33] font-medium hover:text-[#173127] underline decoration-[#8FA998]/60 underline-offset-[3px] decoration-1 hover:decoration-[#1E3F33] transition-colors"
                    >
                      Request access
                    </Link>
                    .
                  </div>
                </div>

                {isPreview && (
                  <div
                    data-testid="login-demo-shortcuts"
                    className="mt-5 rounded-xl bg-[#FBFAF6] border border-[#EDEAE0]"
                  >
                    <button
                      type="button"
                      data-testid="login-demo-toggle"
                      onClick={() => setDemoOpen((v) => !v)}
                      className="w-full flex items-center justify-between px-3.5 py-2.5 text-left rounded-xl hover:bg-[#F6F4EC] transition-colors"
                    >
                      <span className="text-[11px] uppercase tracking-[0.12em] font-semibold text-[#6B7280]">
                        Preview · demo accounts
                      </span>
                      {demoOpen ? (
                        <CaretUp size={12} className="text-[#6B7280]" />
                      ) : (
                        <CaretDown size={12} className="text-[#6B7280]" />
                      )}
                    </button>
                    {demoOpen && (
                      <div
                        data-testid="login-demo-list"
                        className="px-3.5 pb-3 pt-1 space-y-1"
                      >
                        {DEMO_ACCOUNTS.map((d) => (
                          <button
                            key={d.email}
                            type="button"
                            data-testid={`login-demo-${d.email.split("@")[0]}`}
                            onClick={() => fillDemo(d.email)}
                            className="w-full text-left px-2.5 py-1.5 rounded-md hover:bg-white border border-transparent hover:border-[#E5E3DB] transition-all"
                          >
                            <div className="text-[12.5px] text-[#1F2937] font-medium leading-tight">
                              {d.label}
                            </div>
                            <div className="text-[11px] text-[#6B7280] mt-0.5">
                              {d.email} · {d.role}
                            </div>
                          </button>
                        ))}
                        <p className="text-[10.5px] text-[#9AA0A6] px-2.5 pt-1.5 leading-relaxed">
                          Preview only. These shortcuts are never shown in production.
                        </p>
                      </div>
                    )}
                  </div>
                )}
                {/* FP3-102 — FERPA-supporting privacy statement. Calm,
                    always-visible panel at the bottom of the card (never
                    tooltip/hidden). Wording intentionally says "intended
                    to support FERPA-compliant use" — never "certified" /
                    "approved" / "guaranteed compliant". */}
                <div
                  data-testid="login-privacy-statement"
                  className="mt-5 rounded-xl bg-[#E8F0EB] border border-[#1E3F33]/15 px-4 py-3.5 flex items-start gap-3"
                >
                  <div className="flex-shrink-0 mt-0.5 w-8 h-8 rounded-lg bg-white flex items-center justify-center border border-[#1E3F33]/10">
                    <ShieldCheck
                      size={17}
                      weight="duotone"
                      aria-hidden="true"
                      className="text-[#1E3F33]"
                    />
                  </div>
                  <div className="min-w-0">
                    <div className="text-[11px] uppercase tracking-[0.12em] font-semibold text-[#1E3F33] mb-1">
                      Privacy &amp; student data
                    </div>
                    <p className="text-[12.5px] text-[#374151] leading-relaxed">
                      Designed with role-based access, organization-level data
                      isolation, and privacy controls intended to support
                      FERPA-compliant use by schools.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* FP3-047B — Prominent Explore callout below the sign-in
                card. Replaces the small text link from FP3-047 so
                first-time visitors immediately see the public
                Explore surface. Designed to feel like a calm "learn
                more" panel, not a flashy upsell. */}
            <div
              data-testid="login-explore-callout"
              className="mt-5 rounded-2xl border border-[#1E3F33]/15 bg-white p-4 sm:p-5 shadow-[0_2px_14px_rgba(31,41,55,0.04)]"
            >
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 mt-0.5">
                  <div className="w-9 h-9 rounded-xl bg-[#E8F0EB] flex items-center justify-center">
                    <Sparkle size={16} weight="duotone" className="text-[#1E3F33]" />
                  </div>
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-[13.5px] font-semibold text-[#1F2937] heading-font leading-tight">
                    New to Frame Practice?
                  </div>
                  <p className="text-[12px] text-[#4B5563] mt-1 leading-relaxed">
                    Explore features for administrators and providers — caseloads,
                    reports, service fidelity, and team workflows.
                  </p>
                  <Link
                    to="/explore"
                    data-testid="login-explore-link"
                    className="mt-3 inline-flex items-center justify-center gap-1.5 h-9 px-3.5 rounded-lg border border-[#1E3F33] text-[#1E3F33] hover:bg-[#E8F0EB] text-[12.5px] font-semibold tracking-tight transition-colors"
                  >
                    Explore Frame Practice
                    <ArrowRight size={13} weight="bold" />
                  </Link>
                </div>
              </div>
            </div>

            <p className="text-center text-[11px] text-[#9AA0A6] mt-5">
              © Frame Practice · Pilot &amp; invite-based provider workspace
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function FeatureRow({ icon, title, body }) {
  return (
    <div className="flex items-start gap-2.5 py-1.5">
      <div className="mt-0.5">{icon}</div>
      <div className="leading-snug">
        <div className="text-[13px] font-semibold text-[#1F2937] heading-font">
          {title}
        </div>
        <div className="text-[12.5px] text-[#6B7280]">{body}</div>
      </div>
    </div>
  );
}
